// src/Product/Inventory/InventoryItems.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Plus, Search, UploadCloud, Trash2 } from "lucide-react";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import InventoryItemFormModal from "./InventoryItemFormModal";
import PostMovementModal from "./PostMovementModal";
import InventoryItemsImportModal from "./InventoryItemsImportModal";
import "../Accounting/FixedAssets/fixedAssets.css";

export default function InventoryItems() {
  const { companyId, companyRole } = useCompany();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [showMovMod, setShowMovMod] = useState(false);

  const [showImport, setShowImport] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    const { data, error } = await supabase
      .from("inventory_items")
      .select(
        "id,sku,name,item_type,unit,is_active,buffer_stock,reorder_level,sales_price,inventory_account_id,cogs_account_id,adjustment_account_id"
      )
      .eq("company_id", companyId)
      .order("name", { ascending: true });

    if (!error) setItems(data || []);
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((i) => `${i.sku} ${i.name}`.toLowerCase().includes(s));
  }, [items, q]);

  // ✅ Admin-only delete item (with journal/movements cleanup via edge)
  async function deleteItem(itemId) {
    if (!companyId) return;
    const ok = window.confirm(
      "Delete this inventory item?\n\nThis may be blocked if it has stock balances or movements."
    );
    if (!ok) return;

    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "inventory-item-delete",
        {
          body: { company_id: companyId, item_id: itemId },
        }
      );

      if (error) {
        const msg =
          error?.message ||
          data?.error ||
          "Failed to delete item (edge function error).";
        throw new Error(msg);
      }
      if (data?.error) throw new Error(data.error);

      await load();
    } catch (e) {
      alert(e?.message || "Failed to delete item.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fa-wrap">
      <div className="fa-header">
        <div className="fa-title">
          <h2>Inventory Items</h2>
          <p>
            Create stock items, set buffer stock & mappings (Inventory / COGS /
            Adjustment).
          </p>

          {/* existing feature kept */}
          <button
            className="fa-btn"
            onClick={() => setShowMovMod(true)}
            disabled={busy}
          >
            Post Movement Modal
          </button>
        </div>

        <div className="fa-actions">
          <div className="fa-toolbar" style={{ margin: 0 }}>
            <Search size={16} style={{ opacity: 0.75 }} />
            <input
              className="fa-input"
              placeholder="Search SKU or name…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              disabled={busy}
            />
          </div>

          {/* ✅ existing feature kept */}
          <button
            className="fa-btn"
            onClick={() => setShowImport(true)}
            title="Upload inventory items (CSV/XLSX)"
            disabled={busy}
          >
            <UploadCloud size={16} /> Upload Items
          </button>

          <button
            className="fa-btn fa-btn-primary"
            onClick={() => {
              setEditItem(null);
              setOpen(true);
            }}
            disabled={busy}
          >
            <Plus size={16} /> Add Item
          </button>
        </div>
      </div>

      <div className="fa-panel">
        <table className="fa-table">
          <thead>
            <tr>
              <th>SKU</th>
              <th>Name</th>
              <th>Type</th>
              <th>Unit</th>
              <th>Buffer</th>
              <th>Reorder</th>
              <th>Status</th>
              <th style={{ width: 220 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((i) => (
              <tr key={i.id}>
                <td>{i.sku}</td>
                <td style={{ fontWeight: 900 }}>{i.name}</td>
                <td>{i.item_type}</td>
                <td>{i.unit}</td>
                <td>{Number(i.buffer_stock || 0)}</td>
                <td>{Number(i.reorder_level || 0)}</td>
                <td>{i.is_active ? "Active" : "Inactive"}</td>
                <td style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    className="fa-btn"
                    onClick={() => {
                      setEditItem(i);
                      setOpen(true);
                    }}
                    disabled={busy}
                  >
                    Edit
                  </button>

                  {/* ✅ ADMIN ONLY: Delete */}
                  {companyRole === "admin" && (
                    <button
                      className="fa-btn"
                      onClick={() => deleteItem(i.id)}
                      disabled={busy}
                      title="Delete item (admin only)"
                    >
                      <Trash2 size={16} /> Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={8} style={{ opacity: 0.75 }}>
                  No items found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {open && (
        <InventoryItemFormModal
          item={editItem}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false);
            load();
          }}
        />
      )}

      {showMovMod && (
        <PostMovementModal
          companyId={companyId}
          onClose={() => setShowMovMod(false)}
          onPosted={() => {
            setShowMovMod(false);
            // keep existing behavior
          }}
        />
      )}

      <InventoryItemsImportModal
        open={showImport}
        onClose={() => setShowImport(false)}
        companyId={companyId}
        onImported={() => {
          load();
        }}
      />
    </div>
  );
}
